import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandPreferencesForm } from "@/components/brand-preferences/BrandPreferencesForm";
import { apiClient } from "@/lib/api";
// Use relative path since we are having alias resolution issues
import { BrandPreferencesProfile } from "../../types/brand-preferences";

export default function CreateCampaignPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentBookingWindow, setCurrentBookingWindow] = useState<any>(null);
  const [defaultProfile, setDefaultProfile] = useState<BrandPreferencesProfile | undefined>(undefined);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    async function fetchData() {
      setIsFetchingProfile(true);
      try {
        // Check if a specific window ID was passed in the URL
        const windowIdFromUrl = searchParams.get('window');
        
        let bookingWindow = null;
        if (windowIdFromUrl) {
          // Fetch the specific window that was selected
          try {
            bookingWindow = await apiClient.get(`/api/v1/booking-windows/${windowIdFromUrl}`);
          } catch (err) {
            console.error("Failed to fetch selected booking window, falling back to current", err);
          }
        }
        
        // Fall back to current booking window if no specific window or fetch failed
        if (!bookingWindow) {
          bookingWindow = await apiClient.getCurrentBookingWindow();
        }
        
        setCurrentBookingWindow(bookingWindow);

        // Fetch user's brand profile
        // Get current user/brand context first
        const { brand } = await apiClient.getBrandCurrentUser();
        
        if (brand?._id) {
          // Fetch default profile for this brand
          const profiles = await apiClient.get<BrandPreferencesProfile[]>(`/brand-preferences/brands/${brand._id}/profiles`);
          
          // Find the default profile, or the most recent one
          const defaultProf = profiles.find(p => p.is_default) || profiles[0];
          
          if (defaultProf) {
            setDefaultProfile(defaultProf);
            toast.info("Loaded your default brand preferences.");
          }
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
        // Don't block creation if fetching fails, just let them start fresh
      } finally {
        setIsFetchingProfile(false);
      }
    }
    fetchData();
  }, [searchParams]);

  const handleSubmit = async (values: any) => {
    if (!currentBookingWindow) {
      toast.error("No active booking window found. Cannot create campaign.");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Create a profile for this campaign
      // Ensure we don't send the _id from the default profile if we are copying it
      const { _id, ...profileValues } = values;
      
      // We don't actually need to create a profile separately if the campaign creation endpoint
      // handles the requirements directly. However, the current flow seems to want to persist
      // a specific profile for this campaign.
      // Let's stick to the existing pattern but ensure it works.
      
      // 2. Create the campaign linked to this profile
      // Map budget range enum to a number
      const budgetRange = values.budget_ranges?.[0] || "";
      let budgetMax = 10000; // Default
      
      if (values.target_investment_max) {
          budgetMax = Math.max(1000, values.target_investment_max);
      } else {
          switch (budgetRange) {
            case "under_10k": budgetMax = 10000; break;
            case "10k_50k": budgetMax = 50000; break;
            case "50k_100k": budgetMax = 100000; break;
            case "100k_500k": budgetMax = 500000; break;
            case "500k_1m": budgetMax = 1000000; break;
            case "over_1m": budgetMax = 2000000; break;
            default:
                if (typeof budgetRange === 'string') {
                     const parsed = parseInt(budgetRange.replace(/[^0-9]/g, ""));
                     if (!isNaN(parsed)) budgetMax = parsed;
                }
                break;
          }
      }

      // Handle date parsing safely
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3); // Default 3 months

      await apiClient.createCampaign({
        name: values.profile_name || values.campaign_name || "New Campaign",
        window_id: currentBookingWindow?._id || currentBookingWindow?.id,
        status: "submitted",
        requirements: {
          budget_usd: budgetMax,
          campaign_start_date: startDate.toISOString(),
          campaign_end_date: endDate.toISOString(),
          target_audience: values.target_demographics || [],
          suitability_filters: [
              values.brand_safety_level,
              ...(values.safety_filters || []),
              ...(values.tv_content_ratings || [])
          ].filter(Boolean), // Filter out undefined/null
          success_metrics: [],
          integration_preferences: [], // Removed from simplified form
          min_audience_size: null, // Removed from simplified form
          min_integration_opportunities: null, // Removed from simplified form
          preferred_verticals: values.preferred_genres || [],
          target_categories: values.preferred_genres || [], // Map preferred_genres to target_categories for display
          tone_preferences: [
              values.brand_safety_level,
              ...(values.safety_filters || []),
              ...(values.tv_content_ratings || [])
          ].filter(Boolean), // Map suitability filters to tone_preferences for display
          target_ecpm: 0
        }
      });

      // Invalidate the brand campaigns query so the dashboard updates
      queryClient.invalidateQueries({ queryKey: ['brandCampaigns'] });

      toast.success("Campaign created successfully!");
      navigate("/brand-dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create campaign");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetchingProfile) {
     return (
        <div className="min-h-screen bg-background p-6 md:p-12 flex items-center justify-center">
           <div className="text-center">Loading your preferences...</div>
        </div>
     )
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Create New Campaign</h1>
            <p className="text-muted-foreground mt-1">
              {defaultProfile 
                ? "Review and adjust your brand preferences for this specific campaign."
                : "Submit your requirements for the upcoming booking window."}
            </p>
          </div>
        </div>

        {/* Booking Window Info */}
        {currentBookingWindow ? (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
            <span className="font-semibold">Active Booking Window:</span> {currentBookingWindow.name}
            <span className="mx-2">•</span>
            Ends: {new Date(currentBookingWindow.submission_end_date || currentBookingWindow.end_date).toLocaleDateString()}
          </div>
        ) : (
           <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-sm text-amber-800">
            <span className="font-semibold">No Active Booking Window:</span> You cannot submit a campaign at this time. Please check back later.
          </div>
        )}

        {/* Form Container */}
        <div className="bg-white border rounded-xl shadow-sm p-6 md:p-8">
          <BrandPreferencesForm 
            onSubmit={handleSubmit}
            isLoading={isLoading}
            initialValues={defaultProfile ? {
              ...defaultProfile,
              profile_name: "", // Clear name so they can name the campaign
            } : {
              profile_name: "",
            }}
          />
        </div>
      </div>
    </div>
  );
}
